"use client";

import Image from "next/image";
import { Check, Loader } from "lucide-react";
import { PricingItem, Pricing as PricingType } from "@/types/blocks/pricing";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icon";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAppContext } from "@/contexts/app";
import { useLocale } from "next-intl";

export default function Pricing({
  pricing,
  isStandalone = false,
}: {
  pricing: PricingType;
  isStandalone?: boolean;
}) {
  if (pricing.disabled) {
    return null;
  }

  const description = pricing.description
    ?.replace(/\s*Support:\s*support@seedance2\.space\.?/i, "")
    .trim();

  const checkoutPaused =
    process.env.NEXT_PUBLIC_PAUSE_CHECKOUT === "true";

  const locale = useLocale();

  const { setShowSignModal } = useAppContext();

  const [group, setGroup] = useState(() => {
    // First look for a group with is_featured set to true
    const featuredGroup = pricing.groups?.find((g) => g.is_featured);
    // If no featured group exists, fall back to the first group
    return featuredGroup?.name || pricing.groups?.[0]?.name;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);

  const isAuthError = (message?: string | null) => {
    if (!message) {
      return false;
    }

    const normalized = message.toLowerCase();
    return (
      normalized.includes("no auth") ||
      normalized.includes("unauth") ||
      normalized.includes("sign-in") ||
      normalized.includes("sign in")
    );
  };

  const handleCheckout = async (item: PricingItem, cn_pay: boolean = false) => {
    try {
      const params = {
        product_id: item.product_id,
        currency: cn_pay ? "cny" : item.currency,
        locale: locale || "en",
      };

      setIsLoading(true);
      setProductId(item.product_id);

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        setShowSignModal(true);
        return;
      }

      if (!payload) {
        toast.error("checkout failed");
        return;
      }

      const { code, message, data } = payload;
      if (code !== 0) {
        if (isAuthError(message)) {
          setShowSignModal(true);
          return;
        }

        toast.error(message || "checkout failed");
        return;
      }

      const { checkout_url } = data;
      if (!checkout_url) {
        toast.error("checkout failed");
        return;
      }

      window.location.href = checkout_url;
    } catch (e) {
      console.log("checkout failed: ", e);

      toast.error("checkout failed");
    } finally {
      setIsLoading(false);
      setProductId(null);
    }
  };

  useEffect(() => {
    if (pricing.items) {
      const featuredItem = pricing.items.find((i) => i.is_featured);
      setProductId(featuredItem?.product_id || pricing.items[0]?.product_id);
      setIsLoading(false);
    }
  }, [pricing.items]);

  const TitleTag = isStandalone ? "h1" : "h2";

  return (
    <section id={pricing.name} className="landing-section py-16">
      <div className="container">
        <div className="mx-auto mb-12 text-center">
          <TitleTag className="mb-4 text-4xl font-semibold lg:text-5xl">
            {pricing.title}
          </TitleTag>
          <p className="text-muted-foreground lg:text-lg">{description}</p>
        </div>
        <div className="w-full flex flex-col items-center gap-1">
          {pricing.groups && pricing.groups.length > 0 && (
            <div className="flex h-12 mb-12 items-center rounded-md bg-muted p-1 text-lg">
              <RadioGroup
                value={group}
                className="h-full grid"
                style={{ gridTemplateColumns: `repeat(${pricing.groups.length}, minmax(0, 1fr))` }}
                onValueChange={(value) => {
                  setGroup(value);
                }}
              >
                {pricing.groups.map((item, i) => {
                  return (
                    <div
                      key={i}
                      className='h-full rounded-md transition-all has-[button[data-state="checked"]]:bg-card'
                    >
                      <RadioGroupItem
                        value={item.name || ""}
                        id={item.name}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={item.name}
                        className="flex h-full cursor-pointer items-center justify-center px-4 font-semibold text-muted-foreground peer-data-[state=checked]:text-primary"
                      >
                        {item.title}
                        {item.label && (
                          <Badge
                            variant="outline"
                            className="border-primary bg-primary px-1.5 ml-1 text-primary-foreground"
                          >
                            {item.label}
                          </Badge>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          )}
          <div
            className={`w-full mt-0 grid gap-6 md:grid-cols-${
              pricing.items?.filter(
                (item) => !item.group || item.group === group
              )?.length
            }`}
          >
            {pricing.items?.map((item, index) => {
              if (item.group && item.group !== group) {
                return null;
              }

              const isItemLoading =
                isLoading && productId === item.product_id;

              return (
                <div
                  key={index}
                  className={`rounded-lg p-6 ${
                    item.is_featured
                      ? "border-primary border-2 bg-card text-card-foreground"
                      : "border-muted border"
                  }`}
                >
                  <div className="flex h-full flex-col justify-between gap-5">
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        {item.title && (
                          <h3 className="text-xl font-semibold">
                            {item.title}
                          </h3>
                        )}
                        <div className="flex-1"></div>
                        {item.label && (
                          <Badge
                            variant="outline"
                            className="border-primary bg-primary px-1.5 text-primary-foreground"
                          >
                            {item.label}
                          </Badge>
                        )}
                      </div>
                      <div className="mb-4">
                        <div className="flex items-end gap-2">
                          {item.price && (
                            <span className="text-5xl font-semibold">
                              {item.price}
                            </span>
                          )}
                          {item.unit && (
                            <span className="block font-semibold">
                              {item.unit}
                            </span>
                          )}
                        </div>
                        {item.original_price && (
                          <div className="mt-2 text-xl text-muted-foreground font-semibold line-through">
                            {item.original_price}
                          </div>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-muted-foreground">
                          {item.description}
                        </p>
                      )}
                      {item.features_title && (
                        <p className="mb-3 mt-6 font-semibold">
                          {item.features_title}
                        </p>
                      )}
                      {item.features && (
                        <ul className="flex flex-col gap-3">
                          {item.features.map((feature, fi) => {
                            return (
                              <li className="flex gap-2" key={`feature-${fi}`}>
                                <Check className="mt-1 size-4 shrink-0" />
                                {feature}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {item.cn_amount && item.cn_amount > 0 ? (
                        !checkoutPaused && (
                          <div className="flex items-center gap-x-2 mt-2">
                            <span className="text-sm">人民币支付 👉</span>
                            <div
                              className="inline-block p-2 hover:cursor-pointer hover:bg-muted rounded-md"
                              onClick={() => {
                                if (isLoading) {
                                  return;
                                }
                                handleCheckout(item, true);
                              }}
                            >
                              <Image
                                src="https://pub-e1eb76428e24457ebfc067c635cb4fc4.r2.dev/imgs/cnpay.png"
                                alt="CNY payment"
                                className="h-10 w-20 rounded-lg object-contain"
                                width={80}
                                height={40}
                                sizes="80px"
                                loading="lazy"
                              />
                            </div>
                          </div>
                        )
                      ) : null}
                      {checkoutPaused ? (
                        <p className="text-sm text-muted-foreground text-center">
                          New orders are not being accepted at this time.
                        </p>
                      ) : item.button ? (
                        <Button
                          className="w-full flex items-center justify-center gap-2 font-semibold"
                          disabled={isItemLoading}
                          onClick={() => {
                            if (isLoading) {
                              return;
                            }
                            handleCheckout(item);
                          }}
                        >
                          {(!isLoading || !isItemLoading) && (
                            <p>{item.button.title}</p>
                          )}

                          {isItemLoading && (
                            <p>{item.button.title}</p>
                          )}
                          {isItemLoading && (
                            <Loader className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {item.button.icon && (
                            <Icon name={item.button.icon} className="size-4" />
                          )}
                        </Button>
                      ) : null}
                      {item.tip && (
                        <p className="text-muted-foreground text-sm mt-2">
                          {item.tip}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
